import { Injectable, inject } from '@angular/core';
import { EMPTY, Subscription, catchError, exhaustMap, firstValueFrom, timer } from 'rxjs';
import { AuthStore } from '../../../core/auth/state/auth.store';
import { Rights } from '../../../core/constants/rights.constants';
import { APP_POLLING_INTERVALS } from '../../../core/constants/polling-intervals.constants';
import { SafeErrorService } from '../../../core/services/safe-error.service';
import { ToastService } from '../../../core/services/toast.service';
import { JivuCommandExecutorService } from './jivu-command-executor.service';
import { JivuCommand } from './jivu-command.models';
import { JivuCommandService } from './jivu-command.service';

const SMART_VIDEO_COMMAND_ID_KEY = 'jivu-command:active-smart-video-command-id';
const SMART_VIDEO_COMMAND_ID_JOB_SEPARATOR = '::';

type SmartVideoCommandState = {
  commandId: string;
  jobId?: string;
};

const STALE_COMMAND_MS = 180_000; // 3 minutes

/** Jitter fraction applied to each poll interval (±15%). */
const POLL_JITTER_FRACTION = 0.15;
/** After this many consecutive idle polls (no actionable command), start backing off. */
const IDLE_BACKOFF_THRESHOLD = 3;
/** Maximum backoff multiplier relative to the base interval. */
const IDLE_BACKOFF_MAX_MULTIPLIER = 2.5;

/** Return base ± up to POLL_JITTER_FRACTION (uniformly distributed). */
function withJitter(base: number): number {
  const delta = base * POLL_JITTER_FRACTION;
  return base + (Math.random() * 2 - 1) * delta;
}

@Injectable({ providedIn: 'root' })
export class JivuCommandPollerService {
  private readonly authStore = inject(AuthStore);
  private readonly commandService = inject(JivuCommandService);
  private readonly executor = inject(JivuCommandExecutorService);
  private readonly toast = inject(ToastService);
  private readonly safeError = inject(SafeErrorService);
  private subscription: Subscription | null = null;
  /** Consecutive polls that returned no actionable command. */
  private idleStreak = 0;

  start(): void {
    if (this.subscription || !this.canPoll()) return;

    // Self-rescheduling loop: each cycle fires a one-shot timer(delay) so the
    // delay can vary per-iteration (jitter + idle-backoff).  exhaustMap prevents
    // overlapping HTTP requests; unsubscribing the root Subscription tears
    // everything down cleanly because every inner sub is added to it.
    this.subscription = new Subscription();
    const scheduleNext = (): void => {
      if (!this.subscription) return;
      const delay = this.nextIntervalMs();
      const inner = timer(delay).pipe(
        exhaustMap(() =>
          this.commandService.heartbeatAndPoll().pipe(
            catchError((error) => {
              this.commandService.lastError.set(this.safeError.message(error, { fallback: 'Jivu Command polling failed.' }));
              return EMPTY;
            })
          )
        )
      ).subscribe((response) => {
        this.handlePollResponse(response);
        inner.unsubscribe();
        scheduleNext();
      });
      this.subscription.add(inner);
    };

    // Fire the first poll immediately (delay 0), then let scheduleNext take over.
    const first = timer(0).pipe(
      exhaustMap(() =>
        this.commandService.heartbeatAndPoll().pipe(
          catchError((error) => {
            this.commandService.lastError.set(this.safeError.message(error, { fallback: 'Jivu Command polling failed.' }));
            return EMPTY;
          })
        )
      )
    ).subscribe((response) => {
      this.handlePollResponse(response);
      first.unsubscribe();
      scheduleNext();
    });
    this.subscription.add(first);
  }

  async pollOnce(skipCommandId?: string): Promise<void> {
    if (!this.canPoll()) return;
    try {
      const response = await firstValueFrom(this.commandService.heartbeatAndPoll());
      this.handlePollResponse(response, skipCommandId);
    } catch (error: any) {
      this.commandService.lastError.set(this.safeError.message(error, { fallback: 'Jivu Command polling failed.' }));
    }
  }

  claimNextQueuedCommandSoon(completedCommandId?: string): void {
    this.commandService.acceptQueuedCommandsOnce();
    window.setTimeout(() => {
      this.pollOnce(completedCommandId).catch(error => {
        console.warn('Jivu Command auto-claim after completion failed.', error);
      });
    }, 250);
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.idleStreak = 0;
  }

  private canPoll(): boolean {
    return this.authStore.isAuthenticated()
      && (this.authStore.isSuperAdmin() || this.authStore.hasRight()(Rights.JivuCommand_Access));
  }

  /**
   * Compute the next poll delay in ms.
   *
   * - Base interval is APP_POLLING_INTERVALS.jivuCommandHeartbeatMs (120 s).
   * - Jitter of ±15% is applied on every cycle so that devices don't
   *   poll in lock-step.
   * - After IDLE_BACKOFF_THRESHOLD consecutive idle polls the base is scaled
   *   up linearly towards IDLE_BACKOFF_MAX_MULTIPLIER × base so quiet devices
   *   back off gradually.  Any activity (a command returned) resets the streak.
   */
  private nextIntervalMs(): number {
    const base = APP_POLLING_INTERVALS.jivuCommandHeartbeatMs;
    const backoffMultiplier = this.idleStreak >= IDLE_BACKOFF_THRESHOLD
      ? Math.min(
          IDLE_BACKOFF_MAX_MULTIPLIER,
          1 + (this.idleStreak - IDLE_BACKOFF_THRESHOLD + 1) * 0.5
        )
      : 1;
    return Math.round(withJitter(base * backoffMultiplier));
  }

  private handlePollResponse(
    response: { command?: JivuCommand | null; activeCommand?: JivuCommand | null; activeCommands?: JivuCommand[] | null },
    skipCommandId?: string
  ): void {
    const command = response.command || this.resumeTargetActiveCommand(response.activeCommands, response.activeCommand);
    if (command && command.id !== skipCommandId) {
      // Activity detected — reset idle streak so the interval snaps back to base.
      this.idleStreak = 0;
      void this.executor.execute(command);
    } else {
      this.idleStreak++;
    }
  }

  private resumeTargetActiveCommand(commands?: JivuCommand[] | null, legacyCommand?: JivuCommand | null): JivuCommand | null {
    const candidates = Array.isArray(commands) && commands.length
      ? commands
      : legacyCommand ? [legacyCommand] : [];
    const command = candidates.find(item =>
      ['claimed', 'running'].includes(item.status)
      && this.sameDevice(item.targetDeviceInstanceId, this.commandService.getDeviceInstanceId()));
    if (!command || !['claimed', 'running'].includes(command.status)) return null;

    if (command.commandType === 'smart_video_render') {
      const smartVideoState = this.readSmartVideoCommandState();
      return smartVideoState?.commandId === command.id ? null : command;
    }

    if (command.commandType === 'asset_sequence_render') {
      return command;
    }

    const currentHandoff = this.commandService.readCurrentHandoff();
    if (currentHandoff?.id !== command.id) return command;

    // Same id is already in localStorage — check if the command has gone stale
    // (no progress update in 3 min means a crash mid-execution).
    const isStale = Date.now() - new Date(command.updatedAtUtc).getTime() > STALE_COMMAND_MS;
    if (isStale) {
      this.toast.show('Resuming interrupted command.', 'info', 4000);
      return command;
    }

    // Healthy running command (updatedAtUtc bumped by progress calls) — skip.
    return null;
  }

  private sameDevice(left?: string | null, right?: string | null): boolean {
    return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
  }

  private readLocalStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private readSmartVideoCommandState(): SmartVideoCommandState | null {
    const raw = this.readLocalStorage(SMART_VIDEO_COMMAND_ID_KEY);
    if (!raw) return null;
    const commandId = this.clean(raw);
    if (!commandId) return null;

    const separatorIndex = raw.indexOf(SMART_VIDEO_COMMAND_ID_JOB_SEPARATOR);
    if (separatorIndex < 0) {
      return { commandId };
    }

    const baseCommandId = this.clean(raw.slice(0, separatorIndex));
    if (!baseCommandId) return null;

    const jobId = this.clean(raw.slice(separatorIndex + SMART_VIDEO_COMMAND_ID_JOB_SEPARATOR.length));
    return { commandId: baseCommandId, jobId: jobId || undefined };
  }

  private clean(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
}
